import { CONNECTOR_LIMITS, CONNECTOR_OP_LIMITS, CONNECTOR_OP_OUTPUT_PATTERN } from "@sagot/constants";
import type { ConnectorCallOutput, ConnectorCallStep, HttpRequestStep } from "../types";

/**
 * Pure helpers of the "Call connector operation" form (SD-SC-12): what an operation declares (typed parameters, outputs),
 * what the step fills from it, the sample a test sends and the checks the server repeats. The server judges the saved step
 * (`references_connector_op.py`, `graph_connector.py`); these only let the form say so first.
 */

/** A `{{name}}` or `{{name | fallback}}`: the name is captured. */
const PLACEHOLDER_NAME = /\{\{\s*([a-z_][a-z0-9_.]*)\s*(?:\|[^{}]*)?\}\}/g;
const SAMPLE_KEY = /^[a-z][a-z0-9_.]{0,59}$/;
const VARIABLE_PREFIX = "var.";

/** Shown as code, never translated: examples that carry `{{variables}}`. */
export const OPERATION_EXAMPLES = { param: "{{var.student_no}}", output: "var.enrol_status" } as const;

/** One parameter of an operation, read from its JSON Schema. */
export interface ParamSpec {
  name: string;
  type: "string" | "integer" | "number" | "boolean" | "array" | "object";
  required: boolean;
  description: string;
  options: readonly string[];
  /** The schema's default: a required parameter with one need not be filled. */
  hasDefault: boolean;
}

const TYPES: ReadonlySet<string> = new Set(["string", "integer", "number", "boolean", "array", "object"]);
const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null && !Array.isArray(value);

/** The parameters an operation declares (`params_schema`), required ones first, then in the schema's order. */
export function paramSpecs(schema: Readonly<Record<string, unknown>> | undefined): ParamSpec[] {
  const properties = schema?.properties;
  if (!isRecord(properties)) return [];
  const required = new Set(Array.isArray(schema?.required) ? schema.required.filter((name): name is string => typeof name === "string") : []);
  const specs = Object.entries(properties).map(([name, raw], index) => {
    const spec = isRecord(raw) ? raw : {};
    const type = typeof spec.type === "string" && TYPES.has(spec.type) ? (spec.type as ParamSpec["type"]) : "string";
    const options = Array.isArray(spec.enum) ? spec.enum.filter((item): item is string => typeof item === "string") : [];
    return { index, spec: { name, type, required: required.has(name), description: typeof spec.description === "string" ? spec.description : "", options, hasDefault: "default" in spec } satisfies ParamSpec };
  });
  return specs.sort((a, b) => Number(b.spec.required) - Number(a.spec.required) || a.index - b.index).map((item) => item.spec);
}

/** The run variables an operation's response mapping fills (its `variable` fields, `var.status`), once each. */
export function declaredOutputs(responseMapping: Readonly<Record<string, unknown>> | undefined): string[] {
  const fields = responseMapping?.fields;
  if (!Array.isArray(fields)) return [];
  const names = fields.flatMap((entry) => (isRecord(entry) && entry.target === "variable" && typeof entry.field === "string" ? [entry.field] : []));
  return [...new Set(names)];
}

// ------------------------------------------------------------------ the step

/** A step of the operation form with nothing chosen yet. */
export const blankCall = (step: Pick<HttpRequestStep, "id" | "name">): ConnectorCallStep => ({
  id: step.id,
  type: "http_request",
  ...(step.name ? { name: step.name } : {}),
  connector_id: "",
  operation_key: "",
});

/** The inline (legacy) form with nothing chosen yet; the step's id, name and success / failure exits are kept. */
export const blankInline = (step: Pick<ConnectorCallStep, "id" | "name" | "success_goto" | "failure_goto">): HttpRequestStep => ({
  id: step.id,
  type: "http_request",
  ...(step.name ? { name: step.name } : {}),
  endpoint_id: "",
  method: "GET",
  timeout_ms: CONNECTOR_LIMITS.timeoutMsDefault,
  map: [],
  ...(step.success_goto ? { success_goto: step.success_goto } : {}),
  ...(step.failure_goto ? { failure_goto: step.failure_goto } : {}),
});

/** Switching to the operation form keeps the id, the name and the two exits both forms have. */
export const callFrom = (step: HttpRequestStep): ConnectorCallStep => ({
  ...blankCall(step),
  ...(step.success_goto ? { success_goto: step.success_goto } : {}),
  ...(step.failure_goto ? { failure_goto: step.failure_goto } : {}),
});

/** A copy of the step without some of its optional fields. */
function without(step: ConnectorCallStep, ...keys: (keyof ConnectorCallStep)[]): ConnectorCallStep {
  const copy = { ...step };
  for (const key of keys) Reflect.deleteProperty(copy, key);
  return copy;
}

/** Another connector or operation: the parameters and outputs of the old one no longer apply. */
export function withOperation(step: ConnectorCallStep, connectorId: string, operationKey: string): ConnectorCallStep {
  if (step.connector_id === connectorId && step.operation_key === operationKey) return step;
  // The parameters, outputs and pinned version belonged to the old operation: only the step's own fields and exits stay.
  return { ...without(step, "params", "outputs", "version"), connector_id: connectorId, operation_key: operationKey };
}

/** The step with one parameter set; an empty value removes it (the server then asks for it if it is required). */
export function withParam(step: ConnectorCallStep, name: string, value: string): ConnectorCallStep {
  const kept = Object.entries(step.params ?? {}).filter(([key]) => key !== name);
  const params = Object.fromEntries(value === "" ? kept : [...kept, [name, value]]);
  const rest = without(step, "params");
  return Object.keys(params).length > 0 ? { ...rest, params } : rest;
}

/** Where the declared output `name` is copied: its own name unless the step renames it; `null` when it is not copied. */
export function outputTarget(step: ConnectorCallStep, name: string): string | null {
  if (!step.outputs) return name;
  return step.outputs.find((output) => output.name === name)?.to ?? null;
}

/**
 * The step with the output `name` copied into `to` (`null`: not copied). No `outputs` means every output under its own name,
 * so the first change writes the whole list out (and the list stays: later conditions can name the variables).
 */
export function withOutput(step: ConnectorCallStep, name: string, to: string | null, declared: readonly string[]): ConnectorCallStep {
  const current: ConnectorCallOutput[] = step.outputs ?? declared.map((item) => ({ name: item, to: item }));
  const others = current.filter((output) => output.name !== name);
  const next = to === null ? others : [...others, { name, to }];
  // Declared order first (stable rows), then any output the operation no longer declares (the server names it).
  const ordered = [...declared.flatMap((item) => next.filter((output) => output.name === item)), ...next.filter((output) => !declared.includes(output.name))];
  return { ...step, outputs: ordered };
}

/** The step with every declared output written out under its own name (what "no list" means), so the variables are named in the definition. */
export const withAllOutputs = (step: ConnectorCallStep, declared: readonly string[]): ConnectorCallStep => (step.outputs === undefined && declared.length > 0 ? { ...step, outputs: declared.map((name) => ({ name, to: name })) } : step);

/** The run variables the step fills (what a later condition or message can read). */
export function filledVariables(step: ConnectorCallStep, declared: readonly string[]): string[] {
  return declared.flatMap((name) => {
    const target = outputTarget(step, name);
    return target !== null && CONNECTOR_OP_OUTPUT_PATTERN.test(target) ? [target] : [];
  });
}

/** What the steps after the call can read about it: `step.<id>.ok`, `.status` and `.error`. */
export const operationFact = (step: Pick<ConnectorCallStep, "id">, fact: "ok" | "status" | "error"): string => `{{step.${step.id}.${fact}}}`;

// ------------------------------------------------------------------ checks

export type OutputProblem = "variable" | "duplicate";

/** Why an output row is wrong: not a run variable, or filling a variable another row fills. */
export function outputProblems(step: ConnectorCallStep): Record<string, OutputProblem> {
  const found: Record<string, OutputProblem> = {};
  const seen = new Set<string>();
  for (const output of step.outputs ?? []) {
    if (!CONNECTOR_OP_OUTPUT_PATTERN.test(output.to)) found[output.name] = "variable";
    else if (seen.has(output.to)) found[output.name] = "duplicate";
    seen.add(output.to);
  }
  return found;
}

/** Parameters the operation requires that the step leaves empty. */
export const missingParams = (step: ConnectorCallStep, specs: readonly ParamSpec[]): string[] =>
  specs.filter((spec) => spec.required && !spec.hasDefault && !(step.params?.[spec.name] ?? "").trim()).map((spec) => spec.name);

/** `connector` / `operation` while one is not chosen; `null` when the step names a callable operation. */
export function callIncompleteOf(step: ConnectorCallStep): "connector" | "operation" | null {
  if (!step.connector_id) return "connector";
  return step.operation_key ? null : "operation";
}

/** The text of a template is within the server's limit. */
export const paramTooLong = (value: string): boolean => value.length > CONNECTOR_OP_LIMITS.paramValue;

// ------------------------------------------------------------------ the sample of a test

/** Every `{{variable}}` the parameters use, once each, in the order they appear. */
export function callPlaceholders(step: ConnectorCallStep): string[] {
  const names = new Set<string>();
  for (const text of Object.values(step.params ?? {})) for (const match of text.matchAll(PLACEHOLDER_NAME)) if (match[1]) names.add(match[1]);
  return [...names].filter((name) => SAMPLE_KEY.test(name)).slice(0, CONNECTOR_LIMITS.sampleRows);
}

/** The sample values for the variables the parameters use now: what was typed is kept, a new variable starts empty. */
export const callSample = (step: ConnectorCallStep, typed: Readonly<Record<string, string>>): Record<string, string> => Object.fromEntries(callPlaceholders(step).map((name) => [name, typed[name] ?? ""]));

/** A parameter with the sample put into its `{{variables}}` (an unset one with a fallback takes the fallback, as the run does). */
export function fillTemplate(template: string, sample: Readonly<Record<string, string>>): string {
  return template.replace(/\{\{\s*([a-z_][a-z0-9_.]*)\s*(?:\|\s*([^{}]*?)\s*)?\}\}/g, (_whole, name: string, fallback: string | undefined) => {
    const typed = sample[name];
    return typed !== undefined && typed !== "" ? typed : (fallback ?? "");
  });
}

/** What the test console is sent: the parameters as a run would fill them. */
export function testParamsOf(step: ConnectorCallStep, sample: Readonly<Record<string, string>>): Record<string, string> {
  const filled = Object.entries(step.params ?? {}).map(([name, template]): [string, string] => [name, fillTemplate(template, sample)]);
  return Object.fromEntries(filled.filter(([, value]) => value !== ""));
}

/** Whether "Test" can be sent: an operation is chosen. */
export const callTestProblem = (step: ConnectorCallStep): "connector" | "operation" | null => callIncompleteOf(step);

/** The suggestion for a new row: the declared output's own name. */
export const outputSuggestion = (name: string): string => (name.startsWith(VARIABLE_PREFIX) ? name : `${VARIABLE_PREFIX}${name}`);
